import React, { useEffect, useMemo, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ComposedChart, Area, Scatter, AreaChart,
} from 'recharts';

const C = {
  blue: '#2563eb', green: '#16a34a', purple: '#7c3aed', orange: '#d97706', red: '#dc2626',
  text: '#1e293b', muted: '#64748b', border: '#e2e8f0', bg: '#f8fafc', card: '#fff',
  palette: ['#2563eb', '#16a34a', '#d97706', '#7c3aed', '#dc2626'],
};

const fmt = (n, d = 1) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '-';
  return Number(n).toLocaleString('tr-TR', { maximumFractionDigits: d });
};

const fmtDonem = (d) => {
  const s = String(d || '');
  if (s.length >= 6) return `${s.slice(4, 6)}.${s.slice(0, 4)}`;
  return s || '-';
};

const monthOf = (d) => {
  const s = String(d || '');
  return s.length >= 6 ? parseInt(s.slice(4, 6), 10) : 0;
};

const th = {
  textAlign: 'left', padding: '9px 11px', background: C.bg, borderBottom: `2px solid ${C.border}`,
  fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap',
};
const td = { padding: '8px 11px', borderBottom: `1px solid ${C.border}`, fontSize: 12, color: C.text };

const IP4_STEPS = [
  { id: '4.1', title: '4.1 Su tüketimindeki anomalilerin saptanması ve saha kontrolleri', desc: '13 yöntem çıktıları + saha kontrol önerileri' },
  { id: '4.2', title: '4.2 Tüketim artışındaki en muhtemel faktörler', desc: 'Trend, mevsimsellik, uç değer ve değişkenlik analizi' },
  { id: '4.3', title: '4.3 Zaman içindeki su tüketimi değişimi görselleştirmeleri', desc: 'Park bazlı zaman serisi grafikleri' },
  { id: '4.4', title: '4.4 Park verimlilik analizi görselleştirmeleri', desc: 'Ortalama, CV, trend ve verimlilik skoru' },
  { id: 'C.4.1', title: 'Ç.4.1 Örnek parklar hakkında veri analiz raporları', desc: 'Park başına özet rapor kartları' },
  { id: 'C.4.2', title: 'Ç.4.2 Web tabanlı veri görselleştirmeleri', desc: 'Karşılaştırmalı web grafikleri' },
];

const Section = ({ step, children }) => (
  <div style={{ marginBottom: 28, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
    <div style={{ padding: '14px 16px', borderBottom: `1px solid ${C.border}`, background: 'linear-gradient(90deg,#eff6ff,#f8fafc)' }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>{step.title}</div>
      <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>{step.desc}</div>
    </div>
    <div style={{ padding: 14 }}>{children}</div>
  </div>
);

function fieldControlSuggestion(park) {
  const z = park.methods?.find((m) => m.id === 'zscore');
  const anomalies = z?.points?.filter((p) => p.isAnomaly) || [];
  const lastAnom = anomalies[anomalies.length - 1];
  const consensus = (park.methods || []).filter((m) => (m.stats?.anomalyCount || 0) > 0).length;
  if (!anomalies.length) {
    return { seviye: 'Normal', aksiyon: 'Rutin saha turu yeterli', oncelik: 'Düşük' };
  }
  if (consensus >= 8) {
    return {
      seviye: 'Kritik',
      aksiyon: lastAnom
        ? `Sayaç / hat kontrolü önerilir (son anomali: ${fmtDonem(lastAnom.donem)}, ${fmt(lastAnom.m3)} m³)`
        : 'Acil saha kontrolü',
      oncelik: 'Yüksek',
    };
  }
  if (consensus >= 4) {
    return {
      seviye: 'İzlenmeli',
      aksiyon: 'Sulama programı ve kaçak kontrolü planlansın',
      oncelik: 'Orta',
    };
  }
  return {
    seviye: 'Hafif sapma',
    aksiyon: 'Bir sonraki dönemde tekrar ölçüm',
    oncelik: 'Düşük',
  };
}

function analyzeFactors(park) {
  const series = park.series || [];
  if (series.length < 3) return [];

  const vals = series.map((p) => p.m3);
  const mean = vals.reduce((s, v) => s + v, 0) / vals.length;
  const n = vals.length;
  const xs = vals.map((_, i) => i);
  const xMean = (n - 1) / 2;
  const num = xs.reduce((s, x, i) => s + (x - xMean) * (vals[i] - mean), 0);
  const den = xs.reduce((s, x) => s + (x - xMean) ** 2, 0) || 1e-9;
  const slope = num / den;

  const summer = series.filter((p) => { const m = monthOf(p.donem); return m >= 6 && m <= 9; });
  const winter = series.filter((p) => { const m = monthOf(p.donem); return m === 12 || m <= 2; });
  const summerAvg = summer.length ? summer.reduce((s, p) => s + p.m3, 0) / summer.length : null;
  const winterAvg = winter.length ? winter.reduce((s, p) => s + p.m3, 0) / winter.length : null;
  const seasonalRatio = summerAvg != null && winterAvg != null && winterAvg > 0 ? summerAvg / winterAvg : null;

  const z = park.methods?.find((m) => m.id === 'zscore');
  const anomByMonth = {};
  (z?.points || []).filter((p) => p.isAnomaly).forEach((p) => {
    const m = monthOf(p.donem);
    anomByMonth[m] = (anomByMonth[m] || 0) + 1;
  });
  const peakAnomMonth = Object.entries(anomByMonth).sort((a, b) => b[1] - a[1])[0];

  const recent = vals.slice(-Math.min(6, vals.length));
  const older = vals.slice(0, Math.max(1, vals.length - recent.length));
  const recentAvg = recent.reduce((s, v) => s + v, 0) / recent.length;
  const olderAvg = older.reduce((s, v) => s + v, 0) / older.length;
  const recentLift = olderAvg > 0 ? ((recentAvg - olderAvg) / olderAvg) * 100 : 0;

  const cv = mean > 0 ? (Math.sqrt(vals.reduce((s, v) => s + (v - mean) ** 2, 0) / vals.length) / mean) * 100 : 0;

  const factors = [];
  if (slope > mean * 0.02) {
    factors.push({
      faktor: 'Uzun dönem artan trend',
      etki: 'Yüksek',
      kanit: `Eğim +${fmt(slope, 2)} m³/dönem`,
      aciklama: 'Zamanla tüketim sistematik artıyor (genişleme, kaçak veya program değişimi).',
    });
  } else if (slope < -mean * 0.02) {
    factors.push({
      faktor: 'Azalan trend',
      etki: 'Orta',
      kanit: `Eğim ${fmt(slope, 2)} m³/dönem`,
      aciklama: 'Tüketim düşüyor; verimlilik artışı veya sayaç/arıza ihtimali.',
    });
  }

  if (seasonalRatio != null && seasonalRatio >= 2) {
    factors.push({
      faktor: 'Mevsimsel sulama baskısı (yaz)',
      etki: 'Yüksek',
      kanit: `Yaz/kış oranı ${fmt(seasonalRatio, 2)}×`,
      aciklama: 'Yaz ayları kışa göre belirgin yüksek; iklim ve sulama programı ana sürücü.',
    });
  }

  if (recentLift >= 25) {
    factors.push({
      faktor: 'Son dönemlerde ani artış',
      etki: 'Yüksek',
      kanit: `Son dönemler +${fmt(recentLift, 1)}%`,
      aciklama: 'Yakın dönem ortalaması geçmişe göre yükselmiş; saha kontrolü öncelikli.',
    });
  }

  if (peakAnomMonth) {
    factors.push({
      faktor: `Anomali yoğunluğu: ay ${String(peakAnomMonth[0]).padStart(2, '0')}`,
      etki: 'Orta',
      kanit: `${peakAnomMonth[1]} anomali dönemi`,
      aciklama: 'Belirli aylarda tekrarlayan sapmalar (etkinlik, kurak dönem, arıza).',
    });
  }

  if (cv >= 60) {
    factors.push({
      faktor: 'Yüksek tüketim değişkenliği',
      etki: 'Orta',
      kanit: `CV %${fmt(cv, 1)}`,
      aciklama: 'Dönemler arası dalgalanma yüksek; operasyonel tutarsızlık veya veri kalitesi.',
    });
  }

  if (!factors.length) {
    factors.push({
      faktor: 'Stabil tüketim profili',
      etki: 'Düşük',
      kanit: `Ort. ${fmt(mean)} m³`,
      aciklama: 'Belirgin artış faktörü saptanmadı; rutin izleme yeterli.',
    });
  }

  return factors.map((f) => ({ ...f, park: park.abone_adi, aboneno: park.aboneno }));
}

function efficiencyMetrics(park) {
  const s = park.stats || {};
  const mean = s.mean || 0;
  const std = s.std || 0;
  const cv = mean > 0 ? (std / mean) * 100 : 0;
  const series = park.series || [];
  const vals = series.map((p) => p.m3);
  let slope = 0;
  if (vals.length >= 2) {
    const n = vals.length;
    const xMean = (n - 1) / 2;
    const yMean = mean;
    const num = vals.reduce((acc, v, i) => acc + (i - xMean) * (v - yMean), 0);
    const den = vals.reduce((acc, _, i) => acc + (i - xMean) ** 2, 0) || 1e-9;
    slope = num / den;
  }
  const anomConsensus = (park.methods || []).filter((m) => (m.stats?.anomalyCount || 0) > 0).length;
  // Verimlilik skoru: düşük CV + az anomali + negatif/stabil trend → yüksek
  let score = 100;
  score -= Math.min(40, cv * 0.35);
  score -= Math.min(30, anomConsensus * 2.5);
  if (slope > 0) score -= Math.min(20, (slope / (mean || 1)) * 100);
  score = Math.max(0, Math.min(100, Math.round(score)));

  return {
    aboneno: park.aboneno,
    abone_adi: park.abone_adi,
    ilce_adi: park.ilce_adi,
    n: s.n,
    mean,
    median: s.median,
    std,
    cv,
    slope,
    total: s.total,
    anomMethods: anomConsensus,
    score,
    band: score >= 75 ? 'İyi' : score >= 50 ? 'Orta' : 'Düşük',
  };
}

const AdminRaporIP4 = ({ parks = [] }) => {
  const [chartPark, setChartPark] = useState(null);

  useEffect(() => {
    if (parks.length && !chartPark) setChartPark(parks[0].aboneno);
  }, [parks, chartPark]);

  const activeChartPark = parks.find((p) => p.aboneno === chartPark) || parks[0];

  const anomalyControlRows = useMemo(() => parks.map((p) => {
    const ctrl = fieldControlSuggestion(p);
    const methodHits = (p.methods || [])
      .filter((m) => (m.stats?.anomalyCount || 0) > 0)
      .map((m) => `${m.name}(${m.stats.anomalyCount})`);
    const totalAnoms = (p.methods || []).reduce((s, m) => s + (m.stats?.anomalyCount || 0), 0);
    return {
      aboneno: p.aboneno,
      abone_adi: p.abone_adi,
      ilce_adi: p.ilce_adi,
      donem: p.stats?.n || 0,
      mean: p.stats?.mean,
      totalAnoms,
      methodHits: methodHits.slice(0, 5).join(', ') || '—',
      methodCount: methodHits.length,
      ...ctrl,
    };
  }), [parks]);

  const factorRows = useMemo(() => parks.flatMap(analyzeFactors), [parks]);

  const efficiencyRows = useMemo(() => parks.map(efficiencyMetrics), [parks]);

  const multiSeriesData = useMemo(() => {
    const donemSet = new Set();
    parks.forEach((p) => (p.series || []).forEach((s) => donemSet.add(String(s.donem))));
    const donems = [...donemSet].sort();
    return donems.map((donem) => {
      const row = { donem: fmtDonem(donem), donemRaw: donem };
      parks.forEach((p, i) => {
        const hit = (p.series || []).find((s) => String(s.donem) === donem);
        row[`p${i}`] = hit ? hit.m3 : null;
        row[`name${i}`] = p.abone_adi;
      });
      return row;
    });
  }, [parks]);

  const singleParkSeries = useMemo(() => {
    if (!activeChartPark?.series) return [];
    const mean = activeChartPark.stats?.mean || 0;
    const z = activeChartPark.methods?.find((m) => m.id === 'zscore');
    const anomSet = new Set((z?.points || []).filter((p) => p.isAnomaly).map((p) => String(p.donem)));
    let cum = 0;
    return activeChartPark.series.map((s) => {
      cum += s.m3;
      const isAnom = anomSet.has(String(s.donem));
      return {
        donem: fmtDonem(s.donem),
        m3: s.m3,
        ortalama: mean,
        kumulatif: cum,
        anomali: isAnom ? s.m3 : null,
      };
    });
  }, [activeChartPark]);

  const methodAnomBars = useMemo(() => {
    if (!activeChartPark?.methods) return [];
    return activeChartPark.methods.map((m) => ({
      name: m.name.length > 10 ? `${m.name.slice(0, 10)}…` : m.name,
      full: m.name,
      anomali: m.stats?.anomalyCount || 0,
    }));
  }, [activeChartPark]);

  const monthlyBars = useMemo(() => {
    const buckets = Array.from({ length: 12 }, (_, i) => ({ ay: String(i + 1).padStart(2, '0'), toplam: 0, n: 0 }));
    (activeChartPark?.series || []).forEach((s) => {
      const m = parseInt(String(s.donem).slice(4, 6), 10);
      if (m >= 1 && m <= 12) {
        buckets[m - 1].toplam += s.m3;
        buckets[m - 1].n += 1;
      }
    });
    return buckets.map((b) => ({ ay: b.ay, ort: b.n ? b.toplam / b.n : 0 }));
  }, [activeChartPark]);

  const totalsBar = useMemo(
    () => parks.map((p) => ({
      name: p.abone_adi.length > 14 ? `${p.abone_adi.slice(0, 14)}…` : p.abone_adi,
      toplam: Math.round(p.stats?.total || 0),
      ortalama: Math.round(p.stats?.mean || 0),
    })),
    [parks]
  );

  const efficiencyBarData = useMemo(
    () => efficiencyRows.map((r) => ({
      name: r.abone_adi.length > 16 ? `${r.abone_adi.slice(0, 16)}…` : r.abone_adi,
      skor: r.score,
      cv: Math.round(r.cv),
      ortalama: Math.round(r.mean || 0),
    })),
    [efficiencyRows]
  );

  const radarData = useMemo(() => {
    if (!efficiencyRows.length) return [];
    const keys = [
      { key: 'stability', label: 'Stabilite', get: (r) => Math.max(0, 100 - r.cv) },
      { key: 'score', label: 'Verimlilik', get: (r) => r.score },
      { key: 'anomalyFree', label: 'Anomalisizlik', get: (r) => Math.max(0, 100 - r.anomMethods * 8) },
      { key: 'volume', label: 'Ölçek (norm)', get: (r) => {
        const max = Math.max(...efficiencyRows.map((x) => x.mean || 0), 1);
        return ((r.mean || 0) / max) * 100;
      } },
    ];
    return keys.map((k) => {
      const row = { metric: k.label };
      efficiencyRows.forEach((r, i) => { row[`p${i}`] = Math.round(k.get(r)); });
      return row;
    });
  }, [efficiencyRows]);

  if (!parks.length) {
    return (
      <div style={{ marginTop: 36, padding: 20, color: C.muted, fontSize: 13 }}>
        IP4 için önce örnek park verilerinin yüklenmesi gerekiyor.
      </div>
    );
  }

  return (
    <div style={{ marginTop: 36 }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: C.text }}>
          İş Paketi 4 — Yapay Zeka ile Veri Analizleri ve Görselleştirmeler
        </div>
        <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>
          4.1 → 4.4 ve Ç.4.1–Ç.4.2 — gerçek ASKİ serileri + 13 anomali metodu çıktıları
        </div>
      </div>

      {/* 4.1 */}
      <Section step={IP4_STEPS[0]}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Park', 'İlçe', 'N', 'Ort. (m³)', 'Toplam Anomali İşareti', 'Tespit Eden Metodlar', 'Saha Seviyesi', 'Öncelik', 'Saha Kontrol Önerisi'].map((h) => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {anomalyControlRows.map((r, i) => (
              <tr key={r.aboneno} style={{ background: i % 2 ? C.bg : C.card }}>
                <td style={{ ...td, fontWeight: 600 }}>{r.abone_adi}</td>
                <td style={td}>{r.ilce_adi}</td>
                <td style={td}>{r.donem}</td>
                <td style={td}>{fmt(r.mean)}</td>
                <td style={{ ...td, fontWeight: 700, color: r.totalAnoms > 0 ? C.red : C.green }}>{r.totalAnoms}</td>
                <td style={{ ...td, fontSize: 11, maxWidth: 260 }}>{r.methodHits}</td>
                <td style={td}>{r.seviye}</td>
                <td style={{ ...td, fontWeight: 700, color: r.oncelik === 'Yüksek' ? C.red : r.oncelik === 'Orta' ? C.orange : C.green }}>{r.oncelik}</td>
                <td style={{ ...td, fontSize: 11 }}>{r.aksiyon}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {/* 4.2 */}
      <Section step={IP4_STEPS[1]}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Park', 'Faktör', 'Etki', 'Kanıt (gerçek değer)', 'Açıklama'].map((h) => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {factorRows.map((r, i) => (
              <tr key={`${r.aboneno}-${r.faktor}-${i}`} style={{ background: i % 2 ? C.bg : C.card }}>
                <td style={{ ...td, fontWeight: 600 }}>{r.park}</td>
                <td style={td}>{r.faktor}</td>
                <td style={{
                  ...td, fontWeight: 700,
                  color: r.etki === 'Yüksek' ? C.red : r.etki === 'Orta' ? C.orange : C.green,
                }}>{r.etki}</td>
                <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>{r.kanit}</td>
                <td style={{ ...td, fontSize: 11 }}>{r.aciklama}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {/* 4.3 */}
      <Section step={IP4_STEPS[2]}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {parks.map((p) => (
            <button
              key={p.aboneno}
              type="button"
              onClick={() => setChartPark(p.aboneno)}
              style={{
                padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                border: (activeChartPark?.aboneno === p.aboneno) ? `1px solid ${C.blue}` : `1px solid ${C.border}`,
                background: activeChartPark?.aboneno === p.aboneno ? '#dbeafe' : C.card,
                color: activeChartPark?.aboneno === p.aboneno ? C.blue : C.text,
              }}
            >
              {p.abone_adi.length > 28 ? `${p.abone_adi.slice(0, 28)}…` : p.abone_adi}
            </button>
          ))}
        </div>
        <div style={{ width: '100%', height: 300, marginBottom: 20 }}>
          <ResponsiveContainer>
            <ComposedChart data={singleParkSeries} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="donem" tick={{ fontSize: 10 }} minTickGap={20} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => `${fmt(v)} m³`} />
              <Legend />
              <Area type="monotone" dataKey="m3" name="Tüketim (m³)" fill="#dbeafe" stroke={C.blue} strokeWidth={2} />
              <Line type="monotone" dataKey="ortalama" name="Ortalama" stroke={C.orange} strokeDasharray="4 4" dot={false} />
              <Scatter dataKey="anomali" name="Z-Score anomali" fill={C.red} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Kümülatif tüketim</div>
            <div style={{ width: '100%', height: 220 }}>
              <ResponsiveContainer>
                <AreaChart data={singleParkSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                  <XAxis dataKey="donem" tick={{ fontSize: 9 }} minTickGap={24} />
                  <YAxis tick={{ fontSize: 9 }} />
                  <Tooltip formatter={(v) => `${fmt(v)} m³`} />
                  <Area type="monotone" dataKey="kumulatif" name="Kümülatif" stroke={C.purple} fill="#ede9fe" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Aylık ortalama</div>
            <div style={{ width: '100%', height: 220 }}>
              <ResponsiveContainer>
                <BarChart data={monthlyBars}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                  <XAxis dataKey="ay" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 9 }} />
                  <Tooltip formatter={(v) => `${fmt(v)} m³`} />
                  <Bar dataKey="ort" name="Ay ort." fill={C.green} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>13 metod anomali (seçili park)</div>
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={methodAnomBars} margin={{ bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                  <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} angle={-25} textAnchor="end" height={50} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 9 }} />
                  <Tooltip formatter={(v, _n, ctx) => [v, ctx?.payload?.full]} />
                  <Bar dataKey="anomali" fill={C.red} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Park toplam / ortalama m³</div>
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={totalsBar}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                  <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} angle={-15} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 9 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="toplam" name="Toplam" fill={C.blue} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="ortalama" name="Ortalama" fill={C.orange} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Tüm örnek parklar — karşılaştırma</div>
        <div style={{ width: '100%', height: 320 }}>
          <ResponsiveContainer>
            <LineChart data={multiSeriesData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="donem" tick={{ fontSize: 10 }} minTickGap={24} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend />
              {parks.map((p, i) => (
                <Line
                  key={p.aboneno}
                  type="monotone"
                  dataKey={`p${i}`}
                  name={p.abone_adi.length > 20 ? `${p.abone_adi.slice(0, 20)}…` : p.abone_adi}
                  stroke={C.palette[i % C.palette.length]}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Section>

      {/* 4.4 */}
      <Section step={IP4_STEPS[3]}>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
          <thead>
            <tr>
              {['Park', 'N', 'Ort. (m³)', 'Medyan', 'Std', 'CV %', 'Trend (m³/dönem)', 'Anomali Metod #', 'Verimlilik Skoru', 'Band'].map((h) => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {efficiencyRows.map((r, i) => (
              <tr key={r.aboneno} style={{ background: i % 2 ? C.bg : C.card }}>
                <td style={{ ...td, fontWeight: 600 }}>{r.abone_adi}</td>
                <td style={td}>{r.n}</td>
                <td style={td}>{fmt(r.mean)}</td>
                <td style={td}>{fmt(r.median)}</td>
                <td style={td}>{fmt(r.std)}</td>
                <td style={td}>{fmt(r.cv)}</td>
                <td style={td}>{fmt(r.slope, 2)}</td>
                <td style={td}>{r.anomMethods}/13</td>
                <td style={{ ...td, fontWeight: 800, color: C.blue }}>{r.score}</td>
                <td style={{
                  ...td, fontWeight: 700,
                  color: r.band === 'İyi' ? C.green : r.band === 'Orta' ? C.orange : C.red,
                }}>{r.band}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={efficiencyBarData} margin={{ top: 8, right: 8, left: 0, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} />
              <Tooltip />
              <Legend />
              <Bar dataKey="skor" name="Verimlilik skoru" fill={C.blue} radius={[6, 6, 0, 0]} />
              <Bar dataKey="cv" name="CV %" fill={C.orange} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Section>

      {/* Ç.4.1 */}
      <Section step={IP4_STEPS[4]}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {parks.map((p, i) => {
            const eff = efficiencyRows[i];
            const ctrl = anomalyControlRows[i];
            const factors = factorRows.filter((f) => f.aboneno === p.aboneno).slice(0, 3);
            return (
              <div key={p.aboneno} style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 14, background: C.bg }}>
                <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 6 }}>{p.abone_adi}</div>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>{p.ilce_adi} · Abone {p.aboneno}</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <tbody>
                    {[
                      ['Dönem sayısı', p.stats?.n],
                      ['Ortalama (m³)', fmt(p.stats?.mean)],
                      ['Toplam (m³)', fmt(p.stats?.total)],
                      ['Verimlilik skoru', `${eff?.score} (${eff?.band})`],
                      ['Saha önceliği', ctrl?.oncelik],
                      ['Saha önerisi', ctrl?.aksiyon],
                    ].map(([k, v]) => (
                      <tr key={k}>
                        <td style={{ padding: '4px 0', color: C.muted, verticalAlign: 'top', width: '40%' }}>{k}</td>
                        <td style={{ padding: '4px 0', fontWeight: 600 }}>{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ marginTop: 8, fontSize: 11, color: C.muted, fontWeight: 700 }}>Başlıca faktörler</div>
                <ul style={{ margin: '4px 0 0', paddingLeft: 16, fontSize: 11 }}>
                  {factors.map((f) => (
                    <li key={f.faktor} style={{ marginBottom: 2 }}>{f.faktor} — {f.kanit}</li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Ç.4.2 */}
      <Section step={IP4_STEPS[5]}>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
          Web tabanlı karşılaştırmalı görselleştirmeler (örnek parklar).
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 8 }}>Verimlilik skoru karşılaştırması</div>
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={efficiencyBarData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                  <XAxis dataKey="name" tick={{ fontSize: 9 }} hide />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="skor" fill={C.green} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 8 }}>Çok boyutlu park profili</div>
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <RadarChart data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="metric" tick={{ fontSize: 10 }} />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 9 }} />
                  {parks.map((p, i) => (
                    <Radar
                      key={p.aboneno}
                      name={p.abone_adi.length > 14 ? `${p.abone_adi.slice(0, 14)}…` : p.abone_adi}
                      dataKey={`p${i}`}
                      stroke={C.palette[i % C.palette.length]}
                      fill={C.palette[i % C.palette.length]}
                      fillOpacity={0.15}
                    />
                  ))}
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default AdminRaporIP4;
